// Manages a batch of evaluate tasks: one POST /api/jobs per URL, capped at
// concurrency=2 so qwen3:32b doesn't OOM the local machine. State survives
// navigation + refresh via localStorage key `nice2job.matches.evaluations`.
//
// On each task transition to `done`/`failed`, we invalidate matches queries
// so Pending ↓ / Evaluated ↑ updates without a manual refresh.

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiClientError } from './api';

const STORAGE_KEY = 'nice2job.matches.evaluations';
// Client-side dispatch barrier was removed 2026-05-19 — the server's
// /api/evaluate-bulk creates all jobs in one HTTP shot and the worker
// pool enforces MAX_CONCURRENT server-side, so the SPA-side cap was
// redundant and was racing the bulk POST during dispatch.
const POLL_MS = 2500;

export type EvalStatus = 'queued' | 'pending' | 'running' | 'done' | 'failed';

export interface EvalEntry {
  url: string;
  label: string;           // "Company · Role" for banner display
  company?: string;        // forwarded to backend so LinkedIn/Indeed URLs don't fall back to "Unknown Company"
  role?: string;           // same — backend uses this when JD fetch returns nothing
  jobId: string | null;    // null while queued locally (not yet POSTed)
  status: EvalStatus;
  startedAt: number;
  completedAt?: number;    // client clock when status flipped to done|failed — drives ETA / throughput
  code?: number | null;
  logBytes?: number;       // cumulative stdout/stderr size — powers per-job progress bar
  lastProgressAt?: number; // client clock when logBytes last increased — for stall detection
}

interface PersistedState {
  entries: EvalEntry[];
}

interface JobRecord {
  id: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  code: number | null;
  log_bytes?: number;
  log_mtime?: number;
}

function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { entries: [] };
    const parsed = JSON.parse(raw) as PersistedState;
    if (!Array.isArray(parsed.entries)) return { entries: [] };
    return parsed;
  } catch {
    return { entries: [] };
  }
}

function saveState(s: PersistedState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

export interface UseJobRunnerGroupResult {
  entries: EvalEntry[];
  anyRunning: boolean;
  allDone: boolean;
  doneCount: number;
  failedCount: number;
  start: (items: Array<{ url: string; label: string; company?: string; role?: string }>) => void;
  cancel: (url: string) => Promise<void>;
  cancelAll: () => Promise<void>;
  dismiss: () => void;
}

export function useJobRunnerGroup(): UseJobRunnerGroupResult {
  const qc = useQueryClient();
  const [entries, setEntries] = useState<EvalEntry[]>(() => loadState().entries);
  const prevStatuses = useRef<Record<string, EvalStatus>>({});
  // (concurrency cap + drain effect lived here pre-2026-05-19 — see
  //  file header comment for why they were removed.)

  // Persist on every change
  useEffect(() => {
    saveState({ entries });
  }, [entries]);

  // Mount-time sanity check: drop queued entries whose URL already has
  // a report on the server. Without this, the localStorage queue from
  // a previous "Evaluate all" click silently re-evaluates URLs that
  // were wiped server-side and then re-scraped (Settings → Danger
  // zone wipe + Full run pattern). User reported "scan 后的 role 没有
  //到 pending 直接到 evaluated 了" — the cause was a stale queue
  // from an earlier session re-firing /api/jobs for the SAME URLs.
  //
  // Only runs on hook mount (empty deps). Failures fail-open: if the
  // /api/matches probe errors, we keep the queue intact rather than
  // accidentally dropping valid work.
  const sanityCheckRanRef = useRef(false);
  useEffect(() => {
    if (sanityCheckRanRef.current) return;
    sanityCheckRanRef.current = true;
    const queuedAtMount = loadState().entries.filter((e) => e.status === 'queued');
    if (queuedAtMount.length === 0) return;
    apiGet<{ items: Array<{ url?: string | null }> }>('/api/matches', { limit: 500 })
      .then((r) => {
        const evaluatedUrls = new Set(
          (r.items || []).map((it) => it.url).filter(Boolean) as string[]
        );
        if (evaluatedUrls.size === 0) return;
        setEntries((cur) => {
          const next = cur.filter(
            (e) => !(e.status === 'queued' && evaluatedUrls.has(e.url))
          );
          const dropped = cur.length - next.length;
          if (dropped > 0) {
            console.log(
              `[useJobRunnerGroup] sanity check dropped ${dropped} queued entr${dropped === 1 ? 'y' : 'ies'} (already evaluated server-side)`
            );
          }
          return next;
        });
      })
      .catch(() => { /* keep queue; safer than over-clearing */ });
  }, []);

  // Cross-device reattach: pick up evaluate jobs the SAME user kicked off
  // on another device (or in another tab) so the banner shows them too.
  //
  // Without this, "Evaluate all" on device A is invisible on device B —
  // the local queue lives entirely in B's localStorage. Worse, B is
  // tempted to re-click Evaluate all, racing duplicate jobs.
  //
  // Post-2026-05-19: only `evaluate` jobs need to be picked up here.
  // The screen_eval cache-warmer command and the auto-eval-after-scan
  // chain were removed along with the auto-eval subsystem.
  //
  // Runs once at mount. We query /api/jobs?cmd=evaluate&status=pending,
  // running; for any server-side job whose URL we don't already know
  // about, synthesise an entry. The poll loop above picks it up. We
  // don't try to fold in done/failed jobs — the user already sees
  // those via the Evaluated list, and reattaching to terminal jobs
  // would just clutter the banner.
  const reattachRanRef = useRef(false);
  useEffect(() => {
    if (reattachRanRef.current) return;
    reattachRanRef.current = true;
    (async () => {
      try {
        const res = await apiGet<{ jobs: Array<{
          id: string;
          payload: string;
          status: 'pending' | 'running' | 'done' | 'failed';
          created: number;
        }> }>('/api/jobs?cmd=evaluate&status=pending,running&limit=200');
        const jobs = Array.isArray(res?.jobs) ? res.jobs : [];
        if (jobs.length === 0) return;
        setEntries((cur) => {
          const knownUrls = new Set(cur.map((e) => e.url));
          const knownJobIds = new Set(cur.map((e) => e.jobId).filter(Boolean) as string[]);
          const additions: EvalEntry[] = [];
          for (const j of jobs) {
            if (knownJobIds.has(j.id)) continue;
            let url = '', company = '', role = '';
            try {
              if (j.payload) {
                if (j.payload.startsWith('http')) {
                  url = j.payload;
                } else {
                  const obj = JSON.parse(j.payload);
                  url = String(obj.url || '');
                  company = String(obj.company || '');
                  role = String(obj.role || '');
                }
              }
            } catch { /* payload may be a bare URL string — ignore */ }
            if (!url || knownUrls.has(url)) continue;
            knownUrls.add(url);
            additions.push({
              url,
              label: company || role ? `${company || 'Unknown'} · ${role || ''}`.trim() : url,
              company: company || undefined,
              role: role || undefined,
              jobId: j.id,
              status: (j.status === 'pending' || j.status === 'running') ? j.status : 'pending',
              startedAt: Number(j.created) || Date.now(),
            });
          }
          if (additions.length === 0) return cur;
          console.log(`[useJobRunnerGroup] reattached ${additions.length} in-flight evaluate job${additions.length === 1 ? '' : 's'} from another device/tab`);
          return [...cur, ...additions];
        });
      } catch { /* offline or 401 — caller will see empty banner */ }
    })();
  }, []);

  // When a task flips to done/failed, invalidate matches queries
  useEffect(() => {
    for (const e of entries) {
      const prev = prevStatuses.current[e.url];
      if (prev !== e.status && (e.status === 'done' || e.status === 'failed')) {
        qc.invalidateQueries({ queryKey: ['matches'] });
        qc.invalidateQueries({ queryKey: ['matches-pending'] });
      }
      prevStatuses.current[e.url] = e.status;
    }
  }, [entries, qc]);

  // Poll each non-terminal jobId.
  //
  // Two correctness traps the previous version fell into when N got
  // large (a 275-URL "Evaluate all" was the reproducer the user reported
  // — banner stuck on "0 done · 275 running" forever):
  //
  //   1. `entries` was in the deps. Each per-job setEntries triggered a
  //      re-render, which re-mounted this effect, which restarted the
  //      polling loop from job #1. With 275 entries we never got past
  //      the first few before the next setEntries killed the loop.
  //   2. Sequential awaits at ~50 ms RTT × 275 = ~14 s per tick, but the
  //      interval was 2.5 s — multiple ticks ran concurrently, hammering
  //      the server with redundant polls and creating cascading
  //      setEntries that compounded problem #1.
  //
  // Fix:
  //   - Read entries via a ref so setEntries doesn't restart the effect.
  //     Effect now mounts ONCE per hook lifetime.
  //   - Batch one setEntries per tick, applying ALL collected updates at
  //     the same time (1 re-render per tick, not N).
  //   - Skip overlapping ticks via an inflight flag — if the previous
  //     tick is still running, drop this scheduled invocation.
  const entriesRef = useRef<EvalEntry[]>(entries);
  useEffect(() => { entriesRef.current = entries; }, [entries]);

  useEffect(() => {
    let inflight = false;
    let stopped = false;

    const tick = async () => {
      if (inflight || stopped) return;
      const active = entriesRef.current.filter(
        (e) => e.jobId && (e.status === 'pending' || e.status === 'running')
      );
      if (active.length === 0) return;
      inflight = true;
      // Collect all per-job updates in this map, then apply in ONE
      // setEntries below. Critical for big batches — without batching,
      // 275 polls = 275 React renders per tick.
      const updates = new Map<string, { rec?: JobRecord; missing?: boolean }>();
      try {
        for (const e of active) {
          if (stopped) return;
          try {
            const rec = await apiGet<JobRecord>(`/api/jobs/${e.jobId}`);
            updates.set(e.url, { rec });
          } catch (err) {
            if (err instanceof ApiClientError && err.status === 404) {
              updates.set(e.url, { missing: true });
            }
          }
        }
      } finally {
        inflight = false;
      }
      if (stopped || updates.size === 0) return;
      // Split: terminal status updates → map; 404 (missing) → drop.
      // 404 means the job file no longer exists server-side — either
      // the user explicitly cancelled it (DELETE /api/jobs/:id) or the
      // file-janitor cleaned up a finished one. Either way the entry
      // is stale localStorage cruft, not a real failure. Pre-fix we
      // marked them `failed code=-1`, which surfaced as confusing
      // "exit -1" rows in the EvaluationBanner long after the user
      // had cancelled them.
      const missingUrls = new Set<string>();
      for (const [url, u] of updates) {
        if (u.missing) missingUrls.add(url);
      }
      setEntries((cur) =>
        cur
          .filter((x) => !missingUrls.has(x.url))
          .map((x) => {
            const u = updates.get(x.url);
            if (!u || u.missing) return x;
            const rec = u.rec!;
            const nextBytes = rec.log_bytes ?? x.logBytes ?? 0;
            const grew = nextBytes > (x.logBytes ?? 0);
            const nextStatus = rec.status as EvalStatus;
            // Stamp completedAt the FIRST time we observe a terminal status.
            // ETA / throughput compute against the earliest completedAt (=
            // when this client first saw any completion), so old batches
            // that were queued days ago but only now started processing get
            // a realistic throughput instead of "62 done / 36h = 0.0/min".
            const isTerminal = nextStatus === 'done' || nextStatus === 'failed';
            return {
              ...x,
              status: nextStatus,
              code: rec.code,
              logBytes: nextBytes,
              lastProgressAt: grew ? Date.now() : (x.lastProgressAt ?? Date.now()),
              completedAt: isTerminal && !x.completedAt ? Date.now() : x.completedAt,
            };
          })
      );
    };

    // Run once immediately so a fresh "Evaluate all" click sees status
    // updates within the first 2.5 s, then on a steady cadence.
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, []); // ← run ONCE per hook; entries is read via ref above.

  // Bulk-queue ALL items via POST /api/evaluate-bulk in one shot. The
  // server creates N `evaluate` jobs immediately and returns the per-URL
  // job_id mapping, so by the time setEntries below runs, every entry
  // already has a server-side job_id and the work survives the browser
  // closing.
  //
  // History (pre-bulk): this added entries with status='queued' +
  // jobId=null and let a per-URL drain effect POST them one-by-one.
  // Closing the tab killed the dispatcher → only the first ~5 ever
  // made it to /api/jobs. Then bulk was added as a parallel path with
  // the drain effect still in place as fallback — but those two paths
  // raced on the 1-2s the bulk request was in flight, dispatching every
  // URL TWICE. The drain was removed 2026-05-19. Bulk failure now
  // surfaces as an explicit alert; user re-clicks Evaluate all to retry.
  const start = useCallback((items: Array<{ url: string; label: string; company?: string; role?: string }>) => {
    if (items.length === 0) return;
    // De-dupe against the existing queue first so re-clicks don't double-queue.
    const existingUrls = new Set(entries.map((e) => e.url));
    const fresh = items.filter((it) => !existingUrls.has(it.url));
    if (fresh.length === 0) return;

    // Insert all fresh items as 'queued' first so the UI updates immediately
    // (the bulk POST may take a beat for 250 items). Then patch in the
    // returned job_ids when the response lands.
    setEntries((cur) => {
      const additions: EvalEntry[] = fresh.map((it) => ({
        url: it.url,
        label: it.label,
        company: it.company,
        role: it.role,
        jobId: null,
        status: 'queued',
        startedAt: Date.now(),
      }));
      return [...cur, ...additions];
    });

    // Fire the bulk POST. On success, swap in job_ids + flip to 'pending'
    // (which the polling effect picks up). On failure, mark entries as
    // 'failed' so the user can re-click Evaluate all to retry — there is
    // no more per-URL drain fallback. The drain was removed 2026-05-19
    // because it raced with the bulk POST: during the 1-2s the bulk
    // request was in flight, drain saw 'queued' entries and dispatched
    // them per-URL, creating duplicate evaluate jobs for every URL.
    (async () => {
      const freshUrls = new Set(fresh.map((it) => it.url));
      try {
        const res = await apiPost<{
          batchId: string;
          queued: number;
          items: Array<{ url: string; job_id: string }>;
        }>('/api/evaluate-bulk', {
          items: fresh.map((it) => ({
            url: it.url,
            company: it.company || '',
            role: it.role || '',
          })),
        });
        const idByUrl = new Map(res.items.map((m) => [m.url, m.job_id]));
        setEntries((cur) =>
          cur.map((e) => {
            const jid = idByUrl.get(e.url);
            if (!jid || e.status !== 'queued') return e;
            return { ...e, jobId: jid, status: 'pending' as EvalStatus };
          })
        );
      } catch (err) {
        // Bulk POST failed (rate-limit hit, server down, transient
        // network). Mark this batch as failed so the user sees an
        // explicit failure state and can retry. Without an alert + a
        // failed-state flip, fresh items would stay 'queued' forever
        // with no dispatcher to pick them up.
        const msg = err instanceof ApiClientError
          ? `${err.status}: ${err.message}`
          : (err as Error).message;
        console.error('[evaluate-bulk] POST failed:', msg);
        setEntries((cur) =>
          cur.map((e) =>
            freshUrls.has(e.url) && e.status === 'queued'
              ? { ...e, status: 'failed' as EvalStatus, code: -1 }
              : e
          )
        );
        alert(
          `Bulk evaluate POST failed: ${msg}\n\n` +
          `No items were queued. Click Evaluate all again to retry.`
        );
      }
    })();
  }, [entries]);

  // Surface DELETE /api/jobs/:id failures instead of silently swallowing
  // them. Pre-fix: a 4xx/5xx (auth lapsed, server error) wouldn't throw
  // — `fetch` only throws on network failure — so the entry would
  // disappear from UI while the server-side child kept running. User
  // saw "cancel didn't work" without any clue why. Now we throw on
  // !res.ok so the caller can alert. Network errors still count as
  // failures here (caller decides whether to retry or show the alert).
  async function deleteJobOrThrow(jobId: string): Promise<void> {
    const res = await fetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
    if (!res.ok) {
      let body = '';
      try { body = (await res.text()).slice(0, 200); } catch { /* noop */ }
      throw new Error(`HTTP ${res.status}${body ? ': ' + body : ''}`);
    }
  }

  const cancel = useCallback(async (url: string) => {
    const entry = entries.find((e) => e.url === url);
    let cancelError: Error | null = null;
    if (entry?.jobId) {
      try { await deleteJobOrThrow(entry.jobId); }
      catch (e) { cancelError = e as Error; }
    }
    // Always clear the entry from local state — even if the server-side
    // DELETE failed, the user explicitly asked to forget this row. We
    // surface the error separately so they know to investigate (e.g. job
    // running on the server with a stale auth state).
    setEntries((cur) => cur.filter((e) => e.url !== url));
    if (cancelError) {
      // Brief alert. Includes the URL so a user with multiple cancels
      // in flight knows which one had trouble. Stays sync since this
      // runs at the tail of an awaited path.
      alert(`Cancel sent but server reported: ${cancelError.message}\n\nThe row was removed from your list, but the job may still be running on the server. Refresh to check.`);
    }
  }, [entries]);

  // Stop every non-terminal task, keep completed ones (so user can still see
  // which ones succeeded). Done rows stay; queued/pending/running get DELETEd.
  const cancelAll = useCallback(async () => {
    const toKill = entries.filter((e) => e.status !== 'done' && e.status !== 'failed');
    // Single server-side sweep instead of N parallel DELETEs. Reason:
    // useJobRunnerGroup's reattach (line ~189) only fetches the first
    // 200 jobs from GET /api/jobs (server-side hard cap), so a batch of
    // 1157 "Evaluate all" jobs would only see 200 entries here — the
    // other 957 would survive cancel and the SPA would re-attach to a
    // new 200 on its next poll, making it look like cancel "doesn't
    // work / new jobs keep loading". The server endpoint walks all
    // jobs for this user with no limit and kills every pending/running
    // entry whose cmd is in [screen_eval, evaluate].
    let serverErr: Error | null = null;
    let serverKilled = 0;
    try {
      const res = await apiPost<{ cancelled: number; ids: string[] }>(
        '/api/jobs/cancel-all',
        { cmds: ['screen_eval', 'evaluate'] }
      );
      serverKilled = res?.cancelled || 0;
    } catch (e) {
      serverErr = e as Error;
    }
    // Drop every non-terminal entry from local state regardless of server
    // result — the user explicitly asked to clear this list. If the server
    // sweep errored we surface that separately so they know to refresh
    // and check.
    setEntries((cur) => cur.filter((e) => e.status === 'done' || e.status === 'failed'));
    if (serverErr) {
      const msg = serverErr instanceof ApiClientError
        ? `${serverErr.status}: ${serverErr.message}`
        : serverErr.message;
      alert(
        `Cancel-all sweep failed at server (${msg}).\n\n` +
        `The list was cleared locally, but jobs may still be running on the server.\n` +
        `Refresh to see what survived; you can click Cancel all again to retry.`
      );
    } else if (serverKilled > toKill.length) {
      // Informational: server killed MORE jobs than the SPA was tracking
      // (the rest were server-only — e.g. queued by a previous browser
      // session or an auto-eval batch that exceeded the 200-entry SPA
      // visibility cap). No alert needed — this is the desired behavior.
      console.info(`[cancelAll] server killed ${serverKilled} (SPA was tracking ${toKill.length})`);
    }
  }, [entries]);

  const dismiss = useCallback(() => {
    setEntries([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const anyRunning = entries.some((e) => e.status === 'queued' || e.status === 'pending' || e.status === 'running');
  const doneCount = entries.filter((e) => e.status === 'done').length;
  const failedCount = entries.filter((e) => e.status === 'failed').length;
  const allDone = entries.length > 0 && !anyRunning;

  return { entries, anyRunning, allDone, doneCount, failedCount, start, cancel, cancelAll, dismiss };
}
